import { X } from 'lucide-react';
import { useState, type ReactNode } from 'react';

/*
  ONE TREATMENT, AND IT IS THE BRAND'S OWN PALE YELLOW (03 Nov 2026, product call).

  There were five tones — slate, blue, emerald, amber, red — and `tone` is GONE rather than
  ignored: a prop accepted and never read is a dead field, which is the same call that took
  `updatedBy`/`updatedAt` off `<ProfileHeader>` and all 31 of its call sites.

  **The words carry the state, and the icon carries it again.** A tinted strip said the same
  thing a third time, in the one vocabulary the reader cannot read precisely: every banner on
  every profile competed for the same attention, and a blue "currently posted to X" read as
  loudly as a red "this record is deleted". Grey puts the sentence back in charge.

  **What is given up, deliberately**: a deleted record's banner is no longer red and a locked
  one no longer amber. Both still say so in words, and the profile's status PILL is the
  always-on state indicator that was never the banner's job to duplicate.

  **`<NextActionAlert>` went the same way the same day** — it is the other strip, with a glyph
  disc and a CTA, and it is what the index page's prerequisites banner draws. The two now share
  one ground and one raised edge; keep them in step.

  The RAISED edge is what replaces the tint as the thing that separates it from the panel —
  a white inset top highlight over a two-step drop shadow. Written LITERALLY: a Tailwind class
  built from a template literal generates no CSS and fails silently.
*/
const RAISED =
  'shadow-[inset_0_1px_0_0_rgb(255_255_255_/_0.9),0_1px_2px_0_rgb(15_23_42_/_0.08),0_2px_4px_-1px_rgb(15_23_42_/_0.06)]';

/*
  SUBTLE YELLOW, BLACK LABEL (03 Nov 2026, product call) — REPLACING the grey of the same day.

  Grey was the right correction to six competing tones and the wrong resting state: flattened,
  the strip receded so far into the panel it was sitting on that the raised edge was doing all
  the work of saying it was there at all. Brand yellow at its LIGHTEST step is the product's own
  chrome, which is what this strip is: it is the shell saying something, not the record.

  THE 'NEVER ON A RECORD'S STATE' RULE IS NARROWED, NOT BROKEN. A status PILL reports state and
  stays off the brand ramp. This strip reports what the SHELL wants next, and it carries exactly
  one tint for every one of those — there is still no ramp to read a severity off.

  `brand-50` (#fefbea) with a `brand-200` edge, and the text is `slate-900`: at 17.2:1 the
  label is the strongest thing on the strip, which is the point of a black font on a pale tint.
  (This is the one place content reaches 900 — the selection tone is otherwise reserved, and
  the trade is deliberate: the strip has no selected thing to be confused with.)

  Written LITERALLY: a Tailwind class built from a template literal generates no CSS and fails
  silently.
*/
const BANNER = `bg-brand-50 text-slate-900 ring-brand-200 ${RAISED}`;

/*
  THE ONE CARVE-OUT: a FAILURE is red (03 Nov 2026, same day, after the flattening).

  `error` is a BOOLEAN and not a tone, for the reason the tone went: a ramp grows back. There is
  exactly one state that earns a colour, so it is spelled as a yes/no that nothing can widen.
  `<NextActionAlert>` carries the identical pair — the two strips stay in step.

  The product's own red, not a new value. A record being DELETED is not an error and stays grey:
  it is a state the record is in, which the status pill already says.
*/
const BANNER_ERROR = `bg-red-50 text-red-800 ring-red-200 ${RAISED}`;

/**
 * The banner at the top of a profile's content — the record's state in one line, with the
 * action that changes it on the right.
 *
 * **Every profile page has one.** It states what the record currently is, for every state
 * and not only the bad ones: a banner that appears only when something is wrong teaches the
 * reader to skip the region, and then it is missed on the day it matters.
 *
 * It is the home of the **record-level lifecycle action** — Disable, Enable. Those
 * act on the whole record rather than on one card's fields, so they belong here rather than
 * in a card header or a toolbar of abbreviations.
 *
 * Dismissal is per visit and deliberately not persisted: the state is still true after the
 * banner is closed, so it must come back on the next load rather than being permanently
 * silenced. The identity block's status pill is the always-on indicator that survives it.
 */
export function ProfileBanner({
  error = false,
  icon,
  message,
  action,
  dismissible = true,
  id,
}: {
  /** The banner is reporting a FAILURE — the one state that is not grey. */
  error?: boolean;
  icon?: ReactNode;
  /** The left side. One sentence about what the record is right now. */
  message: ReactNode;
  /** The right side. Optional — a state can be worth stating with nothing to do about it. */
  action?: ReactNode;
  dismissible?: boolean;
  id?: string;
}) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {
    return null;
  }

  return (
    <div
      id={id}
      role="status"
      className={`flex min-w-0 items-center gap-3 rounded-xl px-4 py-3 ring-1 ${error ? BANNER_ERROR : BANNER}`}
    >
      {icon ? (
        <span className="shrink-0" aria-hidden="true">
          {icon}
        </span>
      ) : null}

      <p className="min-w-0 flex-1 text-sm">{message}</p>

      {action ? <div className="shrink-0">{action}</div> : null}

      {dismissible ? (
        <button
          type="button"
          id={id ? `${id}-dismiss` : undefined}
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="shrink-0 rounded-md p-1 opacity-60 transition hover:bg-white/60 hover:opacity-100"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
